import React from 'react';
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google';
import authService from '../services/authService';

interface GoogleSignInProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * GoogleSignIn Component
 * Renders a Google Sign-In button and handles OAuth authentication
 */
const GoogleSignIn: React.FC<GoogleSignInProps> = ({ onSuccess, onError }) => {
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const ENABLE_GOOGLE_AUTH = process.env.REACT_APP_ENABLE_GOOGLE_AUTH !== 'false';

  // Don't render if Google Auth is disabled or no client ID
  if (!ENABLE_GOOGLE_AUTH || !GOOGLE_CLIENT_ID) {
    return null;
  }

  /**
   * Decode JWT token to get user info
   */
  const decodeJWT = (token: string): any => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  };

  /**
   * Handle successful Google login
   */
  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      if (!credentialResponse.credential) {
        throw new Error('No credential received from Google');
      }

      // Decode the JWT token to get user info
      const userInfo = decodeJWT(credentialResponse.credential);
      
      if (!userInfo || !userInfo.email) {
        throw new Error('Failed to get user information from Google');
      }

      console.log('Google login successful:', userInfo);

      // Call backend to create/login user
      await authService.googleLogin(
        userInfo.email,
        userInfo.given_name,
        userInfo.family_name
      );

      // Notify parent component of success
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Google login error:', error);
      const errorMessage = error?.message || 'Google login failed. Please try again.';
      
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  /**
   * Handle Google login error
   */
  const handleGoogleError = () => {
    console.error('Google login failed');
    const errorMessage = 'Google login failed. Please try again.';
    
    if (onError) {
      onError(errorMessage);
    }
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="w-full">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          size="large"
          width="100%"
          text="continue_with"
          shape="rectangular"
          theme="outline"
          logo_alignment="left"
        />
      </div>
    </GoogleOAuthProvider>
  );
};

export default GoogleSignIn;















